<script setup>
import { datadogLogs } from "@datadog/browser-logs";
import { computed, nextTick, onMounted, reactive, ref, toRefs, watch } from "vue";
import { useToast } from "vue-toast-notification";
import { useStore } from "vuex";
import { updateConjunctionRules } from "@/components/ExperiencePage/utils";
import JSONtoHTML from "@/components/forms/SharedComponents/WYSIWYG/JSONtoHTML.vue";
import ApplyButton from "@/components/shared/Button/ApplyButton.vue";
import ArrowRight from "@/components/shared/icons/ArrowRight.vue";
import { ERROR_TIMEOUT } from "@/constants.js";
import { useGetClassesLiteBySession } from "@/providers/classes/useGetClassesLiteBySession";
import { pastSessionDeadline } from "@/util/pastSessionDeadline";
import ClassCard from "./ClassCard.vue";
import TrackSection from "./TrackSection.vue";

const emit = defineEmits(["applyClicked", "observeSection"]);

const props = defineProps({
  sessionId: {
    type: String,
    required: true,
  },
  session: {
    type: Object,
    required: true,
    default: () => {},
  },
  externalLink: {
    type: String,
    default: "",
  },
  registerType: {
    type: String,
    default: "Personalize & Apply",
  },
});

onMounted(async () => {
  await store.dispatch("experience/getInitialFiltersDataSource", {
    in_use: true,
  });
});

const { sessionId, session } = toRefs(props);
const store = useStore();
const toast = useToast();

const timeFrameOptions = computed(() => {
  return store.getters["experience/getTimeframeOptions"];
});

const numCoursesDisplayed = ref(6);
const hostInstitution = ref(undefined);

const academicLink = ref("");
const baseURL = new URL(`${window.location.protocol}//${window.location.host}/results/courses`);

const state = reactive({
  toggleEmpty: false,
  showPage: false,
  isWrapperCourse: false,
  link_text: "",
  host_institution_url_syllabus: "",
  trackCourses: [],
});

const totalRecords = ref(0);
const conjunctionRules = ref(undefined);
const notConjunction = ref([]);
const andConjunction = ref([]);
const orConjunction = ref([]);
const andOrConjunctionRules = ref(undefined);
const anyClassFromLibrary = ref(false);
const anyClassFromList = ref(false);
const track = ref([]);
const isExpanded = ref(false);
const isToggleVisible = ref(false);
const paragraphRef = ref(null);

const isClamped = computed(() => !isExpanded.value);

const toggleExpansion = () => {
  isExpanded.value = !isExpanded.value;
};

const classesData = ref([]);

const academicCredits = computed(() => {
  const max = session?.value?.academic_max_credits ?? 0;
  const min = session?.value?.academic_min_credits ?? 0;

  if (max === 0 && min === 0) return null;
  if (max === min) return max;
  if (max > min && min !== 0) return `up to ${max}`;

  return max || min;
});

const termsCreditsDescription = computed(() => {
  if (hasTracks.value && academicCredits.value) {
    return `Participants will earn ${academicCredits.value} semester credits.`;
  }
  if (hostInstitution.value?.name && formattedTerms.value && academicCredits.value) {
    return `During ${hostInstitution.value?.name}'s ${formattedTerms.value} term, API students will earn ${academicCredits.value} semester credits.`;
  }
  return null;
});

const academicDescription = computed(() => {
  return session?.value?.academic_description;
});

const hasAcademicDescription = computed(() => {
  const academicDescription = session?.value?.academic_description?.content;
  const result = academicDescription && Object.values(academicDescription)[0]?.content;
  return result;
});

const conjuctionHasData = computed(() => andConjunction.value.length || orConjunction.value.length);

const formattedTerms = computed(() => {
  const terms = session.value.terms;
  if (!terms || terms.length === 0) return "";

  const summerTerms = terms.filter((term) => term.session === "Summer" && term.name.match(/Summer \d+/));

  if (summerTerms.length <= 1) return terms[0]?.name || "";

  summerTerms.sort((a, b) => {
    const suffixA = Number.parseInt(a.name.split(" ")[1]) || 0;
    const suffixB = Number.parseInt(b.name.split(" ")[1]) || 0;
    return suffixA - suffixB;
  });

  const numericSuffixes = summerTerms.map((term) => Number.parseInt(term.name.split(" ")[1])).sort((a, b) => a - b);

  const commonPrefix = summerTerms[0].name.split(" ")[0];
  const formattedSuffixes = numericSuffixes.join(", ").replace(/,([^,]*)$/, " &$1");
  return `${commonPrefix} ${formattedSuffixes} combined`;
});

const hasTracks = computed(() => {
  return state.trackCourses.some((obj) => obj.name && obj.name.trim() !== "");
});

watch([session, hostInstitution], async ([newSession, newHostInstitution]) => {
  let param = "";
  baseURL.searchParams.delete("hostInstitution");
  newSession?.class_filter_criteria?.host_institutions?.forEach((hostInstitution) => {
    param = `${hostInstitution?.name}#$#${hostInstitution?.id}`;
    baseURL.searchParams.append("hostInstitution", param);
  });

  baseURL.searchParams.delete("session_id");
  //add session id for correct filtering in results page
  param = `${newSession.name}#$#${newSession.id}`;
  baseURL.searchParams.append("session_id", param);
  academicLink.value = baseURL;
});

const getConjunctionIds = (conjunction, conjunctionRules) => {
  return conjunctionRules?.filter((rule) => rule.conjunction === conjunction).flatMap((rule) => rule.ids);
};

const updateStateWithClassesData = () => {
  state.toggleEmpty = !classesData?.value?.length;
  if (classesData?.value?.length === 1) {
    const [data] = classesData.value[0];
    if (data?.host_institution_url_syllabus && data?.link_text) {
      state.host_institution_url_syllabus = data.host_institution_url_syllabus;
      state.link_text = data.link_text;
      state.isWrapperCourse = true;
    }
  }
};

const enableLiteSearch = computed(() => !!sessionId.value);

const {
  isSuccess: classesIsReady,
  data: coursesData,
  error: coursesError,
} = useGetClassesLiteBySession({
  id: sessionId,
  useQueryOptions: {
    enabled: enableLiteSearch,
  },
});

watch(coursesError, (error) => {
  if (!error) {
    return;
  }

  datadogLogs.logger.warn("Failed to get lite courses.", {
    error: error.message,
    session_id: sessionId.value,
  });
  toast.open({
    message: "Couldn't get courses for this session.",
    type: "error",
    position: "bottom",
    duration: ERROR_TIMEOUT,
  });
});

watch(
  () => coursesData.value,
  (newCoursesData) => {
    // Reset
    state.trackCourses = [];
    state.toggleEmpty = false;
    classesData.value = [];
    totalRecords.value = newCoursesData?.total_classes_by_criteria ?? 0;

    if (newCoursesData?.classes_by_tracks?.length) {
      // Renders track + classes
      state.trackCourses = newCoursesData.classes_by_tracks.filter((track) => track.conjunction !== "not");
    }

    if (newCoursesData?.classes_by_criteria?.length) {
      // Renders class cards (probably user used Any From List)
      classesData.value = newCoursesData.classes_by_criteria;
      updateStateWithClassesData();
    }
  },
);

watch(session, async (newSession) => {
  if (!newSession) return;

  const sessionClassSelectionRules = newSession.session_class_selection_rules;
  const academicDescription = newSession.academic_description?.content;

  if (newSession?.class_filter_criteria || (academicDescription && Object.values(academicDescription)[0]?.content)) {
    state.showPage = true;
  }

  if (sessionClassSelectionRules) {
    const {
      conjunctionRules: newConjunctionRules,
      notConjunction: newNotConjunction,
      andOrConjunctionRules: newAndOrConjunctionRules,
      andConjunction: newAndConjunction,
      orConjunction: newOrConjunction,
      anyClassFromLibrary: newAnyClassFromLibrary,
      anyClassFromList: newAnyClassFromList,
      track: newTrack,
    } = updateConjunctionRules(sessionClassSelectionRules, getConjunctionIds);
    conjunctionRules.value = newConjunctionRules;
    notConjunction.value = newNotConjunction;
    andOrConjunctionRules.value = newAndOrConjunctionRules;
    andConjunction.value = newAndConjunction;
    orConjunction.value = newOrConjunction;
    anyClassFromLibrary.value = newAnyClassFromLibrary;
    anyClassFromList.value = newAnyClassFromList;
    track.value = newTrack;
  }

  if (newSession?.class_filter_criteria) {
    hostInstitution.value = newSession?.class_filter_criteria?.host_institutions?.[0];
  }
});

const isAcademicDescriptionClamped = (element) => {
  const descElement = element.querySelector("#json-to-html");
  const descElementBranded = element.querySelector("#json-to-html-branded");
  if (descElement) {
    return descElement.scrollHeight > descElement.clientHeight;
  } else if (descElementBranded) {
    return descElementBranded.scrollHeight > descElementBranded.clientHeight;
  }
  return false;
};

const checkClamping = () => {
  nextTick(() => {
    const paragraph = paragraphRef.value?.$el ?? paragraphRef.value;
    if (paragraph) {
      isToggleVisible.value = isAcademicDescriptionClamped(paragraph);
    }
  });
};

watch(hasAcademicDescription, (newValue) => {
  if (newValue) {
    checkClamping();
    window.addEventListener("resize", checkClamping);
  }
});

watch([() => state.showPage, () => classesIsReady.value], ([newShowPage, newClassesIsReady]) => {
  if (newShowPage || newClassesIsReady) emit("observeSection", "academicdetails");
});
</script>

<template>
  <div v-if="state.showPage" class="pt-20 px-4 md:px-8 text-indigo-base">
    <h2 data-testid="academics-details-section-title" class="text-4xl mt-4 mb-2 font-bold text-indigo-base">
      ACADEMICS
    </h2>
    <p v-if="state.isWrapperCourse" class="mt-4 mb-2 text-lg">
      <span v-if="academicCredits">
        Participants will earn {{ academicCredits }} semester credits. Additional course details can be found below.
      </span>
      <span v-if="hostInstitution?.name"> View the course catalog for {{ hostInstitution?.name }}: </span>
      <a
        class="text-university-secondary uppercase text-base font-bold"
        target="_blank"
        :href="state.host_institution_url_syllabus"
      >
        {{ ` ${state.link_text}` }}.
      </a>
      (Note: this link opens in a new tab)
    </p>
    <p v-else-if="conjuctionHasData" class="mb-4">
      <span v-if="termsCreditsDescription" data-testid="terms-credits-description-text">
        {{ termsCreditsDescription }}
      </span>
      <span v-if="andConjunction.length && !orConjunction.length">
        Required course details can be found below.
      </span>
      <span v-if="orConjunction.length && !andConjunction.length">
        Additional course details can be found below.
      </span>
      <span v-if="andConjunction.length && orConjunction.length">
        Additional course details, including required courses can be found below.
      </span>
    </p>
    <p v-else class="mt-4 mb-2 text-lg">
      <span v-if="academicCredits">
        Participants will earn {{ academicCredits }} semester credits. Additional course details can be found below.
      </span>
      <span v-if="totalRecords > 1">
        {{ hostInstitution?.name }} has {{ totalRecords }} courses available in the catalog during the
        {{ formattedTerms }} term.
      </span>
      <span v-else-if="totalRecords == 1">
        {{ hostInstitution?.name }} has {{ totalRecords }} course available in available in the catalog.
      </span>
      <span v-else>
        {{ hostInstitution?.name }} has {{ totalRecords }} courses available in available in the catalog.
      </span>
      <span v-if="totalRecords > 6">
        Please see the courses below as a starting point and click 'View More Classes' to see the full catalog if you
        want to continue your search (Opens in a new tab).
      </span>
    </p>

    <JSONtoHTML
      v-if="hasAcademicDescription"
      ref="paragraphRef"
      data-testid="academic-description"
      :tiptap-json="academicDescription"
      class="text-sm font-medium leading-snug text-left mt-2"
      :custom-class="['text-base', isClamped ? 'line-clamp-5' : '']"
    >
      <div v-if="isToggleVisible" class="flex justify-start mt-2">
        <button
          data-testid="toggle-expansion"
          type="button"
          :class="`font-bold text-base cursor-pointer ${secondaryColorClass}`"
          :aria-expanded="isExpanded"
          @click="toggleExpansion"
        >
          {{ isExpanded ? "- Less" : "+ More" }}
        </button>
      </div>
    </JSONtoHTML>

    <div v-if="!state.isWrapperCourse">
      <TrackSection
        v-for="track in state.trackCourses"
        :key="track.name"
        :class-list="track.data"
        :track="track"
        :show="Boolean(getConjunctionIds(track.conjunction, conjunctionRules).length && track.name)"
        :any-class="track.any_class_from_list"
      />

      <div v-if="!conjuctionHasData || anyClassFromLibrary || anyClassFromList" class="mt-4">
        <p v-if="anyClassFromLibrary" class="mb-4">
          Plus any of the below courses.
        </p>
        <ClassCard
          v-for="(classItem, index) in classesData.slice(0, 6)"
          :key="index"
          class="mb-4"
          :classes="classItem"
          :hidden="classesData?.length !== 1"
        />
      </div>
      <p v-if="state.toggleEmpty" class="mt-4 mb-2 text-lg">
        Sorry, no courses available for this session
      </p>

      <div class="mt-20 md:h-40">
        <div class="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 lg:justify-end gap-2 md:gap-4">
          <button
            v-if="totalRecords > 6 && (!conjuctionHasData || anyClassFromLibrary || anyClassFromList)"
            type="button"
            :class="`inline-flex mt-3 md:mt-0 justify-center items-center w-full md:h-6 p-5 text-white ${secondaryColorClassButton} border-[1px]  uppercase font-bold`"
            @click="openAcademic"
          >
            <div>
              <div class="flex gap-1 font-bold uppercase text-base tracking-tight items-center">
                <a :href="academicLink.href" target="_blank" rel="noopener noreferrer">
                  View More Classes
                </a>
                <ArrowRight custom-class="w-5 h-5" />
              </div>
            </div>
          </button>
          <ApplyButton
            :id="sessionId"
            :disabled="pastSessionDeadline(session)"
            type="button"
            :outlined="true"
            class="py-2 mt-3 xs:mt-0 text-sm"
            show-arrow
            :class="totalRecords <= numCoursesDisplayed ? 'md:col-end-3 lg:col-end-3:' : ''"
            :register-type="props.registerType"
            :external-link="props.externalLink"
            event-category="Program Page Personalize CTA"
            @click="emit('applyClicked')"
          />
        </div>
      </div>
    </div>
    <hr class="my-1 border-b-3 md:mt-20 border-gray-200" />
  </div>
</template>
